/* global I18n, messg */
import './styles.scss'

import PropTypes from 'prop-types'
import { kea } from 'kea'
import { put } from 'redux-saga/effects'

import dataURItoBlob from 'lib/utils/data-uri-to-blob'
import uploadFile from 'lib/utils/upload-file'

import railsContext from 'lib/selectors/rails-context'

import headerLogic from '~/scenes/header/logic'

export const uploadUrl = '/profile/edit/upload_avatar'

export default ({ logic, closeAvatarModal = false, NoAvatar = false }) =>
  kea({
    connect: {
      props: [railsContext, ['dpr', 'scene'], logic, ['id'].concat(NoAvatar ? [] : ['avatar as originalAvatar'])],
      actions: [
        headerLogic,
        ['updateAvatar as updateHeaderAvatar'],
        logic,
        ['updateAvatar as updateSceneAvatar']
          .concat(closeAvatarModal ? ['closeAvatarModal'] : [])
          .filter(action => action)
      ]
    },
    path: () => ['scenes', 'avatar'],

    actions: () => ({
      setUploadedAvatar: url => ({ url }),
      setAvatarFormat: avatarFormat => ({ avatarFormat }),
      updateAvatar: avatar => ({ avatar }),
      submit: true,
      submitSuccess: true,
      submitFailure: true,
      setIsSubmitting: isSubmitting => ({ isSubmitting }),
      openWebcam: true,
      closeWebcam: true,
      handleSuccess: response => ({ response }),
      handleError: response => ({ response }),

      webcamStreamReceived: true
    }),

    defaults:
      ({ selectors }) =>
      state => ({
        avatar: selectors.originalAvatar ? selectors.originalAvatar(state) : {}
      }),

    reducers: ({ actions }) => ({
      avatar: [
        {},
        PropTypes.object,
        {
          [actions.updateAvatar]: (_, payload) => payload.avatar
        }
      ],

      uploadedAvatar: [
        '',
        PropTypes.string,
        {
          [actions.setUploadedAvatar]: (_, payload) => {
            return payload.url
          },
          [actions.submitSuccess]: () => null
        }
      ],

      updatedAvatarFormat: [
        null,
        PropTypes.string,
        {
          [actions.setAvatarFormat]: (_, payload) => payload.avatarFormat,
          [actions.setUploadedAvatar]: () => 'custom',
          [actions.submitSuccess]: () => null
        }
      ],
      isSubmitting: [
        false,
        PropTypes.bool,
        {
          [actions.submit]: () => true,
          [actions.setIsSubmitting]: (_, { isSubmitting }) => isSubmitting,
          [actions.submitFailure]: () => false,
          [actions.submitSuccess]: () => false
        }
      ],
      isWebcamOpen: [
        false,
        PropTypes.bool,
        {
          [actions.openWebcam]: () => true,
          [actions.closeWebcam]: () => false
        }
      ]
    }),

    selectors: ({ selectors }) => ({
      avatarFormat: [
        () => [selectors.avatar, selectors.updatedAvatarFormat],
        (avatar, updatedAvatarFormat) => updatedAvatarFormat || avatar.avatarFormat,
        PropTypes.string
      ],
      hasChanges: [
        () => [selectors.avatar, selectors.avatarFormat, selectors.uploadedAvatar],
        (avatar, avatarFormat, uploadedAvatar) => !!uploadedAvatar || avatar.avatarFormat !== avatarFormat,
        PropTypes.bool
      ]
    }),
    listeners: ({ actions }) => ({
      [actions.handleSuccess]: async ({ response }) => {
        const { submitSuccess, updateAvatar, updateHeaderAvatar, closeAvatarModal, updateSceneAvatar } = actions

        if (response.avatar) {
          const { avatarFormat, avatars } = response.avatar

          await updateAvatar(response.avatar)
          await updateHeaderAvatar(avatars[avatarFormat])

          if (updateSceneAvatar) {
            await updateSceneAvatar(avatars[avatarFormat], response.avatar)
          }

          await submitSuccess()
        }

        if (closeAvatarModal) {
          await closeAvatarModal()
        }

        messg.success(I18n.t('profiles.edit.changes_saved'), 2500)
      },

      [actions.handleError]: ({ response }) => {
        const { submitFailure } = actions

        submitFailure()
        messg.error(response || I18n.t('activerecord.errors.models.user.attributes.avatar_custom.wrong_type'), 4000)
      }
    }),

    takeLatest: ({ actions }) => ({
      [actions.submit]: function * () {
        const { submitFailure, submitSuccess, updateAvatar, updateHeaderAvatar, closeAvatarModal, updateSceneAvatar } =
          this.actions
        const { uploadedAvatar, avatarFormat, id } = yield this.fetch('uploadedAvatar', 'avatarFormat', 'id')

        try {
          const file = avatarFormat === 'custom' && uploadedAvatar ? dataURItoBlob(uploadedAvatar) : null
          let idForContactDetails
          if (typeof id === 'function') {
            idForContactDetails = id()
          }
          const response = yield uploadFile(uploadUrl, file, { format: avatarFormat, id: idForContactDetails || id })
          if (response.success) {
            const { avatarFormat, avatars } = response.avatar
            yield put(updateAvatar(response.avatar))
            yield put(updateHeaderAvatar(avatars[avatarFormat]))

            if (updateSceneAvatar) {
              yield put(updateSceneAvatar(avatars[avatarFormat], response.avatar))
            }

            yield put(submitSuccess())

            if (closeAvatarModal) {
              yield put(closeAvatarModal())
            }
            messg.success(I18n.t('profiles.edit.changes_saved'), 2500)
          } else {
            throw new Error(response.error)
          }
        } catch (error) {
          yield put(submitFailure())
          messg.error(error.message, 4000)
        }
      }
    })
  })
